import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Card,
  Table,
  Button,
  Typography,
  Space,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Drawer,
  Tag,
  message,
  Spin,
  Popconfirm,
  Tabs,
  Collapse,
} from 'antd';
import {
  PlusOutlined,
  LinkOutlined,
  ShopOutlined,
  FileSearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../config/api';

const { Title, Text } = Typography;

interface ProblemBusinessRef {
  id: string;
  business_name: string;
}

interface ProblemRow {
  id: string;
  title: string;
  status: string;
  target_date?: string | null;
  owner_first_name?: string | null;
  owner_last_name?: string | null;
  linked_ticket_count?: number;
  root_cause?: string | null;
  businesses?: ProblemBusinessRef[] | null;
}

interface ProactiveTicket {
  ticket_number: string;
  subject?: string | null;
  status: string;
  priority: string;
  created_at: string;
}

interface FrequentBusinessRow {
  business_id: string;
  business_name: string;
  distinct_days: number;
  ticket_count: number;
  tickets: ProactiveTicket[];
}

interface SubjectClusterRow {
  business_id: string;
  business_name: string;
  normalized_subject: string;
  sample_subject: string;
  ticket_count: number;
  tickets: ProactiveTicket[];
}

/** Row shape from GET /api/businesses/search (`Business.search` returns full business columns). */
interface BusinessSearchRow {
  id: string;
  business_name: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  mid?: string | null;
  merchant_id?: string | null;
  store_id?: string | null;
}

function businessSearchOptionLabel(b: BusinessSearchRow) {
  const address = [b.address, b.city, b.state, b.zip_code].filter(Boolean).join(', ');
  const mid = (b.mid || b.merchant_id || '').trim() || '—';
  const storeId =
    b.store_id != null && String(b.store_id).trim() !== '' ? String(b.store_id).trim() : '—';
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minWidth: 0,
        maxWidth: 480,
        padding: '4px 0',
      }}
    >
      <Text strong style={{ fontSize: 13, lineHeight: 1.3 }}>
        {b.business_name}
      </Text>
      <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.35 }}>
        {address || '—'}
      </Text>
      <Text type="secondary" style={{ fontSize: 12 }}>
        MID: {mid}
      </Text>
      <Text type="secondary" style={{ fontSize: 12 }}>
        Store ID: {storeId}
      </Text>
    </div>
  );
}

function makeBusinessOptionRender(rows: BusinessSearchRow[]) {
  return (optOrPayload: any) => {
    const opt = optOrPayload?.option ?? optOrPayload;
    const id = opt?.value as string | undefined;
    const b = id ? rows.find((x) => x.id === id) : undefined;
    if (!b) return <span>{(opt?.label as string) ?? ''}</span>;
    return businessSearchOptionLabel(b);
  };
}

const Problems: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [linkTicketOpen, setLinkTicketOpen] = useState(false);
  const [businessSearch, setBusinessSearch] = useState('');
  const [extraBusinessChoices, setExtraBusinessChoices] = useState<{ id: string; business_name: string }[]>([]);
  const [form] = Form.useForm();
  const [linkForm] = Form.useForm();

  const businessPickerActive = createOpen || !!detailId;

  const { data: businessesInitial } = useQuery(
    ['problems-business-picker-initial'],
    () => api.get('/api/businesses/search?search=&limit=200').then((res) => res.data.businesses || []),
    { enabled: businessPickerActive, staleTime: 5 * 60 * 1000 }
  );

  const { data: businessesSearch = [], isFetching: businessesSearchLoading } = useQuery(
    ['problems-business-picker-search', businessSearch],
    () =>
      api
        .get(`/api/businesses/search?search=${encodeURIComponent(businessSearch)}&limit=200`)
        .then((res) => res.data.businesses || []),
    { enabled: businessPickerActive && businessSearch.length >= 1, staleTime: 30000 }
  );

  const businessSelectOptions = businessSearch.length >= 1 ? businessesSearch : businessesInitial || [];

  const mergedBusinessOptions = useMemo(() => {
    const base = (businessSelectOptions || []) as BusinessSearchRow[];
    const seen = new Set(base.map((b) => b.id));
    const extras = extraBusinessChoices.filter((e) => e.id && !seen.has(e.id)) as BusinessSearchRow[];
    return [...extras, ...base];
  }, [businessSelectOptions, extraBusinessChoices]);

  useEffect(() => {
    if (!createOpen && !detailId) setBusinessSearch('');
  }, [createOpen, detailId]);

  const { data: listData, isLoading: listLoading } = useQuery(
    ['problems-list'],
    async () => (await api.get('/api/problems')).data
  );

  const { data: detailData, isLoading: detailLoading } = useQuery(
    ['problem-detail', detailId],
    async () => (await api.get(`/api/problems/${detailId}`)).data,
    { enabled: !!detailId }
  );

  const detailBusinessExtras = useMemo(() => {
    const list = detailData?.problem?.businesses;
    if (!Array.isArray(list)) return [];
    return list.map((x: ProblemBusinessRef) => ({ id: x.id, business_name: x.business_name }));
  }, [detailData?.problem?.businesses]);

  const mergedBusinessOptionsDetail = useMemo(() => {
    const base = (businessSelectOptions || []) as BusinessSearchRow[];
    const seen = new Set(base.map((b) => b.id));
    const extras = detailBusinessExtras.filter((e) => e.id && !seen.has(e.id)) as BusinessSearchRow[];
    return [...extras, ...base];
  }, [businessSelectOptions, detailBusinessExtras]);

  const renderCreateBusinessOption = useMemo(
    () => makeBusinessOptionRender(mergedBusinessOptions as BusinessSearchRow[]),
    [mergedBusinessOptions]
  );

  const renderDetailBusinessOption = useMemo(
    () => makeBusinessOptionRender(mergedBusinessOptionsDetail as BusinessSearchRow[]),
    [mergedBusinessOptionsDetail]
  );

  const { data: assignable } = useQuery(
    ['users-assignable-problems'],
    async () => (await api.get('/api/users/assignable')).data
  );

  const { data: proactiveData, isLoading: proactiveLoading, refetch: refetchProactive } = useQuery(
    ['problems-proactive-patterns'],
    async () =>
      (
        await api.get('/api/problems/proactive-patterns', {
          params: {
            days: 14,
            minDistinctDays: 3,
            subjectClusterMin: 2,
            maxClustersPerBusiness: 25,
            ticketsPerSubjectCluster: 100,
          },
        })
      ).data,
    { staleTime: 60000 }
  );

  const createMutation = useMutation(
    async (values: Record<string, unknown>) => {
      const ids = Array.isArray(values.business_ids) ? (values.business_ids as string[]) : [];
      const payload = {
        ...values,
        business_ids: ids,
        target_date: values.target_date ? (values.target_date as dayjs.Dayjs).format('YYYY-MM-DD') : null,
      };
      delete (payload as { business_id?: unknown }).business_id;
      await api.post('/api/problems', payload);
    },
    {
      onSuccess: () => {
        message.success('Problem created');
        queryClient.invalidateQueries(['problems-list']);
        setCreateOpen(false);
        form.resetFields();
      },
      onError: () => {
        message.error('Failed to create problem');
      },
    }
  );

  const updateMutation = useMutation(
    async ({ id, values }: { id: string; values: Record<string, unknown> }) => {
      await api.patch(`/api/problems/${id}`, values);
    },
    {
      onSuccess: () => {
        message.success('Updated');
        queryClient.invalidateQueries(['problems-list']);
        queryClient.invalidateQueries(['problem-detail', detailId]);
      },
      onError: () => {
        message.error('Update failed');
      },
    }
  );

  const linkMutation = useMutation(
    async ({ problemId, ticketNumber }: { problemId: string; ticketNumber: string }) => {
      const t = await api.get(`/api/tickets/number/${encodeURIComponent(ticketNumber.trim())}`);
      const ticketId = t.data?.id;
      if (!ticketId) throw new Error('Ticket not found');
      await api.post(`/api/problems/${problemId}/tickets`, { ticket_id: ticketId });
    },
    {
      onSuccess: () => {
        message.success('Ticket linked');
        queryClient.invalidateQueries(['problem-detail', detailId]);
        queryClient.invalidateQueries(['problems-list']);
        setLinkTicketOpen(false);
        linkForm.resetFields();
      },
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: string } } };
        message.error(err?.response?.data?.error || 'Could not link ticket');
      },
    }
  );

  const unlinkMutation = useMutation(
    async ({ problemId, ticketId }: { problemId: string; ticketId: string }) => {
      await api.delete(`/api/problems/${problemId}/tickets/${ticketId}`);
    },
    {
      onSuccess: () => {
        message.success('Unlinked');
        queryClient.invalidateQueries(['problem-detail', detailId]);
        queryClient.invalidateQueries(['problems-list']);
      },
    }
  );

  const deleteProblemMutation = useMutation(
    async (id: string) => {
      await api.delete(`/api/problems/${id}`);
    },
    {
      onSuccess: () => {
        message.success('Problem deleted');
        setDetailId(null);
        queryClient.invalidateQueries(['problems-list']);
      },
      onError: () => {
        message.error('Failed to delete problem');
      },
    }
  );

  const problems: ProblemRow[] = listData?.problems || [];

  /** Subject clusters from API, grouped by business for Repeat subjects UI. */
  const subjectClustersByBusiness = useMemo(() => {
    const clusters = (proactiveData?.subjectClusters as SubjectClusterRow[]) || [];
    const map = new Map<
      string,
      { business_id: string; business_name: string; clusters: SubjectClusterRow[] }
    >();
    for (const row of clusters) {
      if (!row.business_id) continue;
      let g = map.get(row.business_id);
      if (!g) {
        g = {
          business_id: row.business_id,
          business_name: row.business_name || 'Business',
          clusters: [],
        };
        map.set(row.business_id, g);
      }
      g.clusters.push(row);
    }
    const list = Array.from(map.values());
    list.sort((a, b) =>
      (a.business_name || '').localeCompare(b.business_name || '', undefined, { sensitivity: 'base' })
    );
    for (const g of list) {
      g.clusters.sort((a, b) => b.ticket_count - a.ticket_count);
    }
    return list;
  }, [proactiveData?.subjectClusters]);

  const problemTicketsTab = (
    <Card>
      {listLoading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          size="small"
          rowKey="id"
          dataSource={problems}
          onRow={(record) => ({
            onClick: () => setDetailId(record.id),
            style: { cursor: 'pointer' },
          })}
          columns={[
            { title: 'Title', dataIndex: 'title', key: 'title', ellipsis: true },
            {
              title: 'Businesses',
              key: 'businesses',
              width: 260,
              render: (_: unknown, r: ProblemRow) => {
                const list = r.businesses;
                if (!list || list.length === 0) return '—';
                return (
                  <Space size={[4, 4]} wrap onClick={(e) => e.stopPropagation()}>
                    {list.map((b) => (
                      <Button
                        key={b.id}
                        type="link"
                        size="small"
                        style={{ padding: 0, height: 'auto' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/businesses/${b.id}`);
                        }}
                      >
                        {b.business_name}
                      </Button>
                    ))}
                  </Space>
                );
              },
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              width: 120,
              render: (s: string) => {
                const c = s === 'resolved' ? 'green' : s === 'in_progress' ? 'blue' : 'default';
                return <Tag color={c}>{s.replace('_', ' ')}</Tag>;
              },
            },
            {
              title: 'Owner',
              key: 'owner',
              width: 160,
              render: (_: unknown, r: ProblemRow) =>
                r.owner_first_name || r.owner_last_name
                  ? `${r.owner_first_name || ''} ${r.owner_last_name || ''}`.trim()
                  : '—',
            },
            {
              title: 'Target',
              dataIndex: 'target_date',
              key: 'target_date',
              width: 120,
              render: (d: string | null) => (d ? dayjs(d).format('YYYY-MM-DD') : '—'),
            },
            {
              title: 'Tickets',
              dataIndex: 'linked_ticket_count',
              key: 'linked_ticket_count',
              width: 80,
            },
          ]}
          pagination={{ pageSize: 15 }}
        />
      )}
    </Card>
  );

  const patternsTab = (
    <>
      <Card
        style={{ marginBottom: 24 }}
        title={
          <Space>
            <ShopOutlined />
            <span>Repeated contact (by business)</span>
          </Space>
        }
        extra={
          <Button size="small" onClick={() => refetchProactive()}>
            Refresh
          </Button>
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Businesses with new tickets on at least 3 different calendar days in the last 14 days (scoped to tickets you
          can see). Expand a row for recent tickets in that window.
        </Text>
        {proactiveLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : !(proactiveData?.frequentBusinesses as FrequentBusinessRow[] | undefined)?.length ? (
          <Text type="secondary">No businesses meet this pattern right now.</Text>
        ) : (
          <Table<FrequentBusinessRow>
            size="small"
            rowKey="business_id"
            pagination={false}
            dataSource={proactiveData.frequentBusinesses as FrequentBusinessRow[]}
            expandable={{
              expandedRowRender: (row) => (
                <Table<ProactiveTicket>
                  size="small"
                  rowKey="ticket_number"
                  dataSource={row.tickets}
                  pagination={false}
                  columns={[
                    {
                      title: 'Ticket',
                      dataIndex: 'ticket_number',
                      key: 'ticket_number',
                      width: 110,
                      render: (n: string) => (
                        <Button type="link" size="small" onClick={() => navigate(`/workspace/number/${n}`)}>
                          #{n}
                        </Button>
                      ),
                    },
                    { title: 'Subject', dataIndex: 'subject', key: 'subject', ellipsis: true },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      key: 'status',
                      width: 90,
                      render: (s: string) => <Tag>{s}</Tag>,
                    },
                    {
                      title: 'Priority',
                      dataIndex: 'priority',
                      key: 'priority',
                      width: 90,
                    },
                    {
                      title: 'Created',
                      dataIndex: 'created_at',
                      key: 'created_at',
                      width: 150,
                      render: (d: string) => dayjs(d).format('MMM D, YYYY HH:mm'),
                    },
                  ]}
                />
              ),
            }}
            columns={[
              { title: 'Business', dataIndex: 'business_name', key: 'business_name', ellipsis: true },
              {
                title: 'Distinct days',
                dataIndex: 'distinct_days',
                key: 'distinct_days',
                width: 110,
              },
              { title: 'Tickets in window', dataIndex: 'ticket_count', key: 'ticket_count', width: 130 },
            ]}
          />
        )}
      </Card>

      <Card
        title={
          <Space>
            <FileSearchOutlined />
            <span>Repeat subjects (same business)</span>
          </Space>
        }
        extra={
          <Button size="small" onClick={() => refetchProactive()}>
            Refresh
          </Button>
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Grouped by business. Each row is a <strong>keyword</strong> (from subject wording or two words joined, e.g.
          door+dash → doordash). Every ticket whose subject contains that keyword is listed—expand the row to see them
          (at least 2 tickets in the last 14 days). Spaces and punctuation are ignored when matching.
        </Text>
        {proactiveLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : subjectClustersByBusiness.length === 0 ? (
          <Text type="secondary">No repeat subject clusters in this window.</Text>
        ) : (
          <Collapse
            bordered={false}
            style={{ background: 'transparent' }}
            items={subjectClustersByBusiness.map((grp) => ({
              key: grp.business_id,
              label: (
                <Space wrap>
                  <Text strong>{grp.business_name}</Text>
                  <Tag>
                    {grp.clusters.length} subject pattern{grp.clusters.length === 1 ? '' : 's'}
                  </Tag>
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: 'auto' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/businesses/${grp.business_id}`);
                    }}
                  >
                    Open business
                  </Button>
                </Space>
              ),
              children: (
                <Table<SubjectClusterRow>
                  size="small"
                  rowKey={(r) => `${grp.business_id}-${r.normalized_subject}`}
                  pagination={false}
                  dataSource={grp.clusters}
                  expandable={{
                    expandedRowRender: (row) => (
                      <Table<ProactiveTicket>
                        size="small"
                        rowKey="ticket_number"
                        dataSource={row.tickets}
                        pagination={false}
                        columns={[
                          {
                            title: 'Ticket',
                            dataIndex: 'ticket_number',
                            key: 'ticket_number',
                            width: 110,
                            render: (n: string) => (
                              <Button type="link" size="small" onClick={() => navigate(`/workspace/number/${n}`)}>
                                #{n}
                              </Button>
                            ),
                          },
                          { title: 'Subject', dataIndex: 'subject', key: 'subject', ellipsis: true },
                          {
                            title: 'Status',
                            dataIndex: 'status',
                            key: 'status',
                            width: 90,
                            render: (s: string) => <Tag>{s}</Tag>,
                          },
                          {
                            title: 'Created',
                            dataIndex: 'created_at',
                            key: 'created_at',
                            width: 150,
                            render: (d: string) => dayjs(d).format('MMM D, YYYY HH:mm'),
                          },
                        ]}
                      />
                    ),
                  }}
                  columns={[
                    {
                      title: 'Keyword / sample',
                      key: 'kw_sample',
                      ellipsis: true,
                      render: (_: unknown, row: SubjectClusterRow) => (
                        <span>
                          <Text code>{row.normalized_subject}</Text>
                          <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                            {row.sample_subject ? ` · ${row.sample_subject}` : ''}
                          </Text>
                        </span>
                      ),
                    },
                    { title: 'Tickets', dataIndex: 'ticket_count', key: 'ticket_count', width: 90 },
                  ]}
                />
              ),
            }))}
          />
        )}
      </Card>
    </>
  );

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: 'calc(100vh - 64px)' }}>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }} align="center">
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Problems
          </Title>
          <Text type="secondary">
            Track underlying causes for recurring incidents. Link related tickets from the drawer.
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            form.resetFields();
            setBusinessSearch('');
            setExtraBusinessChoices([]);
            setCreateOpen(true);
          }}
        >
          New problem
        </Button>
      </Space>

      <Tabs
        defaultActiveKey="tickets"
        items={[
          { key: 'tickets', label: 'Problem Tickets', children: problemTicketsTab },
          { key: 'patterns', label: 'Patterns', children: patternsTab },
        ]}
      />

      <Modal
        title="New problem"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          form.resetFields();
          setBusinessSearch('');
          setExtraBusinessChoices([]);
        }}
        onOk={() => form.submit()}
        confirmLoading={createMutation.isLoading}
        width={560}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => createMutation.mutate(values)}
        >
          <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="Short title for the underlying issue" />
          </Form.Item>
          <Form.Item
            name="business_ids"
            label="Businesses"
            rules={[
              {
                validator: async (_, v) => {
                  if (Array.isArray(v) && v.length > 0) return;
                  throw new Error('Select at least one business');
                },
              },
            ]}
          >
            <Select
              mode="multiple"
              showSearch
              allowClear
              placeholder="Search and select one or more businesses"
              filterOption={false}
              onSearch={(v) => setBusinessSearch(v)}
              maxTagCount="responsive"
              dropdownStyle={{ minWidth: 420 }}
              dropdownMatchSelectWidth={false}
              notFoundContent={
                businessSearch.length < 1 ? (
                  'Type to filter, or pick from the list below'
                ) : businessesSearchLoading ? (
                  <Spin size="small" />
                ) : (
                  'No businesses found'
                )
              }
              suffixIcon={businessesSearchLoading ? <Spin size="small" /> : undefined}
              options={mergedBusinessOptions.map((b: BusinessSearchRow) => ({
                value: b.id,
                label: b.business_name,
              }))}
              optionFilterProp="label"
              optionRender={renderCreateBusinessOption}
            />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Context, scope, affected businesses" />
          </Form.Item>
          <Form.Item name="root_cause" label="Root cause (when known)">
            <Input.TextArea rows={2} placeholder="Permanent fix or vendor dependency" />
          </Form.Item>
          <Form.Item name="owner_user_id" label="Owner">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Assign owner"
              options={(assignable || []).map((u: { id: string; first_name?: string; last_name?: string }) => ({
                value: u.id,
                label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.id,
              }))}
            />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="open">
            <Select
              options={[
                { value: 'open', label: 'Open' },
                { value: 'in_progress', label: 'In progress' },
                { value: 'resolved', label: 'Resolved' },
              ]}
            />
          </Form.Item>
          <Form.Item name="target_date" label="Target date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="signature_field_key" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="category_id" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="issue_value" hidden>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={detailData?.problem?.title || 'Problem'}
        width={720}
        open={!!detailId}
        onClose={() => {
          setDetailId(null);
          setBusinessSearch('');
        }}
        extra={
          <Space>
            <Button
              icon={<LinkOutlined />}
              onClick={() => {
                setLinkTicketOpen(true);
              }}
            >
              Link ticket
            </Button>
            {detailData?.problem?.id ? (
              <Popconfirm
                title="Delete this problem?"
                description="Removes the record and ticket links. This cannot be undone."
                okText="Delete"
                okButtonProps={{ danger: true, loading: deleteProblemMutation.isLoading }}
                cancelText="Cancel"
                onConfirm={() => deleteProblemMutation.mutate(detailData.problem.id)}
              >
                <Button danger loading={deleteProblemMutation.isLoading}>
                  Delete
                </Button>
              </Popconfirm>
            ) : null}
          </Space>
        }
      >
        {detailLoading || !detailData ? (
          <Spin />
        ) : (
          <>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Text type="secondary">Businesses</Text>
                <Select
                  mode="multiple"
                  style={{ width: '100%', marginTop: 4 }}
                  showSearch
                  placeholder="Search and select one or more businesses"
                  filterOption={false}
                  onSearch={(v) => setBusinessSearch(v)}
                  loading={businessesSearchLoading}
                  maxTagCount="responsive"
                  dropdownStyle={{ minWidth: 420 }}
                  dropdownMatchSelectWidth={false}
                  value={
                    Array.isArray(detailData.problem.businesses)
                      ? detailData.problem.businesses.map((b: ProblemBusinessRef) => b.id)
                      : []
                  }
                  onChange={(business_ids) => {
                    if (!business_ids || business_ids.length === 0) {
                      message.warning('At least one business is required');
                      return;
                    }
                    updateMutation.mutate({
                      id: detailData.problem.id,
                      values: { business_ids },
                    });
                  }}
                  options={mergedBusinessOptionsDetail.map((b: BusinessSearchRow) => ({
                    value: b.id,
                    label: b.business_name,
                  }))}
                  optionFilterProp="label"
                  optionRender={renderDetailBusinessOption}
                  notFoundContent={
                    businessSearch.length < 1 ? (
                      'Type to filter, or pick from the list below'
                    ) : businessesSearchLoading ? (
                      <Spin size="small" />
                    ) : (
                      'No businesses found'
                    )
                  }
                />
              </div>
              <div>
                <Text type="secondary">Status</Text>
                <div>
                  <Select
                    style={{ minWidth: 160 }}
                    value={detailData.problem.status}
                    onChange={(status) =>
                      updateMutation.mutate({ id: detailData.problem.id, values: { status } })
                    }
                    options={[
                      { value: 'open', label: 'Open' },
                      { value: 'in_progress', label: 'In progress' },
                      { value: 'resolved', label: 'Resolved' },
                    ]}
                  />
                </div>
              </div>
              <div>
                <Text type="secondary">Description</Text>
                <Input.TextArea
                  rows={3}
                  defaultValue={detailData.problem.description || ''}
                  onBlur={(e) => {
                    const v = e.target.value;
                    if (v !== (detailData.problem.description || '')) {
                      updateMutation.mutate({ id: detailData.problem.id, values: { description: v } });
                    }
                  }}
                />
              </div>
              <div>
                <Text type="secondary">Root cause</Text>
                <Input.TextArea
                  rows={2}
                  defaultValue={detailData.problem.root_cause || ''}
                  onBlur={(e) => {
                    const v = e.target.value;
                    if (v !== (detailData.problem.root_cause || '')) {
                      updateMutation.mutate({ id: detailData.problem.id, values: { root_cause: v } });
                    }
                  }}
                />
              </div>
            </Space>

            <Title level={5} style={{ marginTop: 24 }}>
              Linked tickets
            </Title>
            <Table
              size="small"
              rowKey="id"
              dataSource={detailData.tickets || []}
              columns={[
                {
                  title: 'Ticket',
                  dataIndex: 'ticket_number',
                  key: 'ticket_number',
                  render: (n: string) => (
                    <Text
                      code
                      style={{ cursor: 'pointer', color: '#1890ff' }}
                      onClick={() => navigate(`/workspace/number/${n}`)}
                    >
                      {n}
                    </Text>
                  ),
                },
                { title: 'Subject', dataIndex: 'subject', key: 'subject', ellipsis: true },
                {
                  title: '',
                  key: 'x',
                  width: 80,
                  render: (_: unknown, row: { id: string }) => (
                    <Button
                      type="link"
                      danger
                      size="small"
                      onClick={() =>
                        unlinkMutation.mutate({ problemId: detailData.problem.id, ticketId: row.id })
                      }
                    >
                      Remove
                    </Button>
                  ),
                },
              ]}
              pagination={false}
            />
          </>
        )}
      </Drawer>

      <Modal
        title="Link ticket by number"
        open={linkTicketOpen}
        onCancel={() => {
          setLinkTicketOpen(false);
          linkForm.resetFields();
        }}
        onOk={() => linkForm.submit()}
        confirmLoading={linkMutation.isLoading}
      >
        <Form
          form={linkForm}
          layout="vertical"
          onFinish={(values) => {
            if (detailId && values.ticket_number) {
              linkMutation.mutate({ problemId: detailId, ticketNumber: values.ticket_number });
            }
          }}
        >
          <Form.Item
            name="ticket_number"
            label="Ticket #"
            rules={[{ required: true, message: 'Enter ticket number' }]}
          >
            <Input placeholder="e.g. 12345" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Problems;
